import React, { useState, useEffect } from 'react';
import ProgramPage from './ProgramPage'; // Importa il componente della pagina Programma

// Componente principale dell'App per il sito web della convention medica
const App = () => {
  // Stato per contenere i dati degli articoli
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Stato per gestire la pagina corrente: 'main' o 'programma'
  const [currentPage, setCurrentPage] = useState('main'); // Inizia sulla pagina principale
  // Nuovo stato per gestire la visibilità della sidebar su schermi piccoli
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    // Funzione per recuperare gli articoli da Strapi
    const fetchArticles = async () => {
      try {
        setLoading(true);
        setError(null); // Pulisci errori precedenti

        // --- IMPORTANTE: Sostituisci 'http://localhost:1337' con l'URL del tuo backend Strapi ---
        // Se il tuo Strapi è stato deployato, usa il suo URL pubblico (es. 'https://your-strapi-app.render.com')
        // Il '?populate=image' è cruciale per includere i dati dell'immagine nella risposta
        const strapiBaseUrl = "https://efficient-birthday-2db46252c3.strapiapp.com"; // Il tuo URL Strapi Cloud
        const response = await fetch(`${strapiBaseUrl}/api/articles?populate=image`);

        if (!response.ok) {
          throw new Error(`Errore HTTP! Stato: ${response.status}. Assicurati che Strapi sia in esecuzione e accessibile.`);
        }

        const data = await response.json();
        console.log("Dati ricevuti da Strapi:", data); // LOG DI DEBUG: Controlla questo nella console del browser!

        // Mappa la struttura dati nidificata di Strapi allo stato del tuo componente
        // Adattato per accedere direttamente alle proprietà dell'oggetto 'item'
        // NOTA: Questa mappatura potrebbe causare l'errore React #31 se i campi sono annidati sotto 'attributes'
        const fetchedArticles = data.data.map(item => ({
          id: item.id,
          title: item.title, // Accesso diretto a 'title'
          description: item.description, // Accesso diretto a 'description'
          // Costruisci l'URL completo dell'immagine. Adatta l'URL di base se Strapi non è su localhost.
          // Assumiamo che l'URL dell'immagine sia direttamente in item.image.url
          imageUrl: item.image?.url ?
                    `${strapiBaseUrl}${item.image.url}` :
                    'https://placehold.co/600x400/CCCCCC/000000?text=Nessuna+Immagine', // Fallback se non c'è immagine
        }));
        setArticles(fetchedArticles);

      } catch (err) {
        console.error("Impossibile recuperare gli articoli:", err);
        setError(err.message);
        setArticles([]); // Non ci sono dati mock, quindi imposta un array vuoto in caso di errore
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []); // L'array di dipendenze vuoto significa che questo effetto viene eseguito una sola volta al montaggio

  // Funzione per renderizzare il contenuto della pagina corrente
  const renderPageContent = () => {
    switch (currentPage) {
      case 'main':
        return (
          // Contenuto della pagina principale (senza header, ora gestito fuori)
          <main className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
            <section className="bg-white p-8 rounded-2xl shadow-xl mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b-4 border-[#bf0030] pb-2">
                Informazioni sulla Convention
              </h2>
              <div className="flex flex-col md:flex-row items-center gap-8 mb-6"> {/* Aggiunto flex container per testo e immagine */}
                <p className="text-gray-700 leading-relaxed mb-4 md:mb-0 md:w-2/3">
                  Questa quinta edizione dell'aggiornamento medico è interamente dedicata alla flebologia e alla medicina di genere, presentando nuovi aggiornamenti prognostici e tematiche specifiche. Saranno discusse le anomalie metaboliche nelle sindromi varicose complicate e la patogenesi delle trombosi post-vaccinazione, con dati da registri retrospettivi e interventi di esperti del settore flebologico, sia dal vivo che da remoto.
                  <br /><br />
                  Il convegno affronterà anche il trattamento estetico delle teleangectasie, con la partecipazione di esperti di medicina estetica e rigenerativa. La medicina di genere sarà esplorata da specialisti nazionali e internazionali, coprendo temi di prevenzione primaria, appropriatezza degli interventi con nuove tecniche e trattamenti a confronto. Saranno inoltre approfonditi aspetti di medicina vascolare e diagnostica per immagini, sia a livello clinico che strumentale.
                  <br /><br />
                  In conclusione, verrà aperta una discussione interdisciplinare con giuristi, che chiariranno le inappropriatezze in ambito medico-legale e definiranno gli aspetti correlati sia in fase di prevenzione che di stato, qualificando ulteriormente questa quinta edizione dell'aggiornamento clinico specialistico.
                </p>
                <div className="md:w-1/3 flex-shrink-0"> {/* Contenitore per l'immagine */}
                  <img
                    src="https://vispdocs.com/wp-content/uploads/2024/07/veins-1024x538.png"
                    alt="Immagine di vasi sanguigni"
                    className="w-full h-auto rounded-lg shadow-md md:rotate-90"
                  />
                </div>
              </div>
              <div className="mt-6">
                <h3 className="text-2xl font-semibold text-gray-700 mb-3">Presiedono e Responsabili Scientifici:</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Prof.ssa Olga Scudiero, Federico II</li>
                  <li>Dott. Pierpaolo Di Micco</li>
                </ul>
              </div>
            </section>

            {/* Sezione Articoli */}
            <section className="bg-white p-8 rounded-2xl shadow-xl mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b-4 border-[#bf0030] pb-2">
                Ultimi Articoli e Approfondimenti
              </h2>

              {loading && (
                <div className="text-center text-gray-600 text-lg py-8">Caricamento articoli...</div>
              )}

              {error && (
                <div className="text-center text-red-600 text-lg py-8">Errore: {error}. Impossibile caricare gli articoli.</div>
              )}

              {!loading && articles.length === 0 && !error && (
                <div className="text-center text-gray-600 text-lg py-8">Nessun articolo trovato.</div>
              )}

              <div className="grid grid-cols-1 gap-8">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </section>
            
            {/* Nuova Sezione Visiva con Immagine di Vasi Sanguigni */}
            <section className="bg-gradient-to-r from-[#bf0030] to-[#ff3366] p-8 rounded-2xl shadow-xl text-white text-center flex flex-col items-center justify-center">
              <h3 className="text-3xl font-bold mb-4">Evento patrocinato da</h3>
              <img
                src="https://cifnazionale.it/wp-content/uploads/2021/01/logoCIF-HD.jpg"
                alt="Immagine CIF"
                className="w-full max-w-xl h-auto rounded-lg shadow-md mt-4"
              />
            </section>

          </main>
        );
      case 'programma':
        return <ProgramPage />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans antialiased flex flex-col">
      {/* Sezione Intestazione - Sempre in alto, a tutta larghezza */}
      <header className="bg-gradient-to-r from-[#bf0030] to-[#8c0022] text-white py-16 px-4 sm:px-6 lg:px-8 shadow-lg rounded-br-3xl relative">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 animate-fade-in-down">
            V CONVEGNO FLEBOLOGIA E MEDICINA DI GENERE
          </h1>
          <p className="text-xl sm:text-2xl mb-8 opacity-90 animate-fade-in-up">
            Aggiornamenti Prognostici e Nuove Prospettive nella Medicina di Genere
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <span className="bg-white text-[#bf0030] px-6 py-3 rounded-full text-lg font-semibold shadow-md transition-transform transform hover:scale-105">
              3 Dicembre 2025
            </span>
            <span className="bg-white text-[#bf0030] px-6 py-3 rounded-full text-lg font-semibold shadow-md transition-transform transform hover:scale-105">
              Federico II, Facoltà di Biotecnologie, Napoli, Via Tommaso De Amicis 95
            </span>
          </div>
        </div>
        {/* Bottone per aprire/chiudere la sidebar su schermi piccoli */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="md:hidden absolute top-4 left-4 p-4 rounded-full bg-white text-[#bf0030] shadow-lg z-50"
          aria-label={isSidebarOpen ? "Chiudi menu" : "Apri menu"}
        >
          {isSidebarOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </header>

      {/* Contenitore per Sidebar e Contenuto Dinamico: flex-row su desktop, flex-col su mobile */}
      <div className="flex flex-grow flex-col md:flex-row">
        {/* Overlay per la sidebar su mobile */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          ></div>
        )}

        {/* Sidebar */}
        <aside
          className={`
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            fixed inset-y-0 left-0 z-50 w-64 bg-gray-800 text-white flex flex-col p-4 shadow-lg h-full
            transition-transform duration-300 ease-in-out
            md:relative md:translate-x-0 md:flex md:flex-col md:w-64 md:flex-shrink-0 md:h-auto md:min-h-full
          `}
        >
          <div className="text-2xl font-bold mb-8 text-center text-[#ff3366]">Menu</div>
          <nav className="flex flex-col space-y-4">
            <button
              onClick={() => { setCurrentPage('main'); setIsSidebarOpen(false); }}
              className="w-full text-left px-6 py-3 rounded-lg bg-[#bf0030] hover:bg-[#8c0022] transition-colors duration-200 font-semibold shadow-md"
            >
              Home
            </button>
            <button
              onClick={() => { setCurrentPage('programma'); setIsSidebarOpen(false); }}
              className="w-full text-left px-6 py-3 rounded-lg bg-[#bf0030] hover:bg-[#8c0022] transition-colors duration-200 font-semibold shadow-md"
            >
              Programma
            </button>
            <a
              href="https://maps.app.goo.gl/uSzMfSGDB3HCDGDz7" // Placeholder per il link esterno 1
              target="_blank"
              rel="noopener noreferrer"
              className="w-full text-left px-6 py-3 rounded-lg bg-[#ff3366] hover:bg-[#bf0030] transition-colors duration-200 font-semibold shadow-md flex items-center justify-between"
            >
              Facility
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSe9sWgQs65EYoaCUR4N6vIs_S2gAgm1deVC4YDIFx53CPEZOg/viewform?usp=dialog" // Placeholder per il link Google Forms
              target="_blank"
              rel="noopener noreferrer"
              className="w-full text-left px-6 py-3 rounded-lg bg-[#ff3366] hover:bg-[#bf0030] transition-colors duration-200 font-semibold shadow-md flex items-center justify-between"
            >
              Iscriviti
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </nav>
        </aside>

        {/* Contenuto Principale Dinamico */}
        <div className="flex-grow p-4 md:p-8 flex flex-col">
          {renderPageContent()}
          
        </div>
      </div>
      {/* Sezione Footer - Spostato qui per essere gestito dal flex-grow del contenuto */}
          <footer className="bg-gray-800 text-white py-8 mt-auto rounded-tr-3xl w-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p>Evento accreditato ECM - Segreteria organizzativa: Fancy Tour.</p>
              <p className="text-sm mt-2">Contattaci: info@fancy.it </p>
            </div>
          </footer>
    </div>
  );
};

// Componente ArticleCard per visualizzare i singoli articoli
const ArticleCard = ({ article }) => {
  return (
    <div className="bg-gray-50 rounded-xl shadow-md overflow-hidden transform transition-transform hover:scale-105 hover:shadow-lg flex flex-col">
      <img
        src={article.imageUrl}
        alt={article.title}
        className="w-full h-48 object-cover"
        onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400/CCCCCC/000000?text=Immagine+non+trovata'; }} // Immagine di fallback
      />
      <div className="p-6 flex-grow flex flex-col">
        <h3 className="text-xl font-semibold text-[#bf0030] mb-2">{article.title}</h3> {/* Colore testo */}
        <p className="text-gray-600 text-sm leading-relaxed mb-4">{article.description}</p>
      </div>
    </div>
  );
};

export default App;
